import { childNodes, cleanFieldValue, controlDefault, fieldKindOf, ownActions, resolveOptions, resolveRecords } from "./uiControlRules.js";
import { dateMedium } from "../wording/dateTime.js";
const isLegacyField = (n) => n.type === "choice-cards" || n.type === "field-text" || n.type === "field-select" || n.type === "field-number" || n.type === "field-toggle";
const isControlField = (n) => !isLegacyField(n) && fieldKindOf(n) !== undefined && typeof n.name === "string";
/** The nodes of a spec, in document order. */
export function walkNodes(root, visit) {
    visit(root);
    for (const c of childNodes(root))
        walkNodes(c, visit);
}
/** The fields of a spec, in document order. */
export function fieldsOf(spec) {
    const out = [];
    walkNodes(spec.root, (n) => {
        if (isLegacyField(n) || isControlField(n))
            out.push(n);
    });
    return out;
}
/** Every action a spec can trigger, in document order. A press is accepted only when its action is one of these. */
export function actionsOf(spec) {
    const out = [];
    walkNodes(spec.root, (n) => {
        if (n.type === "button")
            out.push(n.action);
        else if (n.type === "actions")
            for (const b of n.buttons)
                out.push(b.action);
        else if (n.type === "table")
            for (const r of n.rowActions ?? [])
                out.push(r.action);
        else
            for (const a of ownActions(n))
                out.push(a);
    });
    return out;
}
/** Whether `action` is one the spec carries (compared by value, so a forged press with a different text is refused). */
export function specHasAction(spec, action) {
    const key = JSON.stringify(action);
    return actionsOf(spec).some((a) => JSON.stringify(a) === key);
}
/** What each field holds before the person touches it. */
export function fieldDefaults(spec) {
    const out = {};
    for (const f of fieldsOf(spec)) {
        if (!isLegacyField(f)) {
            const v = controlDefault(f);
            if (v !== undefined)
                out[f.name] = v;
            continue;
        }
        if (f.type === "field-toggle")
            out[f.name] = f.default === true;
        else if (f.type === "field-number") {
            if (typeof f.default === "number")
                out[f.name] = f.default;
        }
        else if (f.default !== undefined)
            out[f.name] = f.default;
    }
    return out;
}
/**
 * The values a press carries, cleaned against the spec: only fields it declares, a select or choice only among its options, a
 * number finite and inside its bounds, a text capped. Anything else is dropped, so what reaches an agent is what the block
 * could have produced.
 */
export function cleanValues(spec, raw) {
    const out = {};
    if (!raw || typeof raw !== "object")
        return out;
    const given = raw;
    for (const f of fieldsOf(spec)) {
        const v = given[f.name];
        if (v === undefined)
            continue;
        if (!isLegacyField(f)) {
            const cleaned = cleanFieldValue(f, v);
            if (cleaned !== undefined)
                out[f.name] = cleaned;
            continue;
        }
        if (f.type === "field-toggle")
            out[f.name] = v === true;
        else if (f.type === "field-number") {
            const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
            if (!Number.isFinite(n))
                continue;
            out[f.name] = Math.min(f.max ?? Infinity, Math.max(f.min ?? -Infinity, n));
        }
        else if (f.type === "field-text") {
            if (typeof v === "string")
                out[f.name] = v.slice(0, 4_000);
        }
        else if (typeof v === "string" && f.options.some((o) => o.value === v))
            out[f.name] = v;
    }
    return out;
}
/** The required fields with no value yet, by label. */
export function missingRequired(spec, values) {
    const out = [];
    for (const f of fieldsOf(spec)) {
        if (!isLegacyField(f)) {
            const v = values[f.name];
            const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
            if (f.required && empty)
                out.push(String(f.label ?? f.name));
            continue;
        }
        if (f.type === "field-toggle" || f.type === "choice-cards") {
            if (f.type === "choice-cards" && values[f.name] === undefined && f.default === undefined)
                out.push(f.label ?? f.name);
            continue;
        }
        if (f.required && (values[f.name] === undefined || String(values[f.name]).trim() === ""))
            out.push(f.label);
    }
    return out;
}
/** What the person answered, as label and value, in document order; an option is named by its label. */
export function answerParts(spec, values) {
    const out = [];
    for (const f of fieldsOf(spec)) {
        const v = values[f.name];
        if (v === undefined || v === "")
            continue;
        if (!isLegacyField(f)) {
            const label = String(f.label ?? f.name);
            if (Array.isArray(v)) {
                const names = new Map(resolveOptions(f, undefined).map((o) => [o.value, o.label]));
                if (v.length)
                    out.push({ name: f.name, label, value: v.map((x) => names.get(x) ?? x).join(", ") });
            }
            else if (typeof v === "boolean")
                out.push({ name: f.name, label, value: v ? "Yes" : "No" });
            else
                out.push({ name: f.name, label, value: resolveOptions(f, undefined).find((o) => o.value === v)?.label ?? String(v) });
            continue;
        }
        if (f.type === "choice-cards" || f.type === "field-select")
            out.push({ name: f.name, label: f.label ?? f.name, value: f.options.find((o) => o.value === v)?.label ?? String(v) });
        else if (f.type === "field-toggle")
            out.push({ name: f.name, label: f.label, value: v === true ? "Yes" : "No" });
        else
            out.push({ name: f.name, label: f.label, value: String(v) });
    }
    return out;
}
/** The line a conversation shows for an answer: "Answered the block · One source of truth · Net 30". */
export function answerLine(spec, values, action) {
    const parts = answerParts(spec, values).map((p) => p.value);
    if (!parts.length && action?.do === "send")
        return `Answered the block · ${action.text}`;
    return ["Answered the block", ...parts].join(" · ");
}
/**
 * The structured user message a press becomes. The agent gets the text the button carries and the values by field name, in a
 * form it can read without guessing; the conversation shows the one-line answer.
 */
export function uiEventMessage(spec, blockId, action, rawValues) {
    const values = cleanValues(spec, rawValues);
    const visible = answerLine(spec, values, action);
    const says = action.do === "send" ? action.text : "";
    const forAgent = [
        `[ui-block-event] The person answered the block you showed (${blockId}).`,
        says ? `Button: ${says}` : "",
        `Answers: ${JSON.stringify(Object.fromEntries(answerParts(spec, values).map((p) => [p.name, { question: p.label, answer: values[p.name], shown: p.value }])))}`,
    ]
        .filter(Boolean)
        .join("\n");
    return { visible, forAgent };
}
// ── The microapp template: {{path|pipe}} over a binding answer ─────────
/** The pipes a placeholder may use. Fixed: there is no expression language to attack. */
export const UI_PIPES = ["count", "first", "last", "sum", "money", "number", "upper", "lower", "date"];
const dig = (data, path) => {
    let cur = data;
    for (const part of path.split(".")) {
        if (cur === null || cur === undefined)
            return undefined;
        if (Array.isArray(cur))
            cur = /^\d+$/.test(part) ? cur[Number(part)] : undefined;
        else if (typeof cur === "object")
            cur = Object.prototype.hasOwnProperty.call(cur, part) ? cur[part] : undefined;
        else
            return undefined;
    }
    return cur;
};
const asText = (v) => (v === undefined || v === null ? "" : Array.isArray(v) ? v.filter((x) => typeof x === "string" || typeof x === "number").join(",") : typeof v === "object" ? "" : String(v));
function applyPipe(value, pipe) {
    const [name, arg] = pipe.split(":");
    switch (name) {
        case "count":
            return Array.isArray(value) ? value.length : value && typeof value === "object" ? Object.keys(value).length : value === undefined || value === null ? 0 : 1;
        case "first":
            return Array.isArray(value) ? value[0] : value;
        case "last":
            return Array.isArray(value) ? value[value.length - 1] : value;
        case "sum":
            return Array.isArray(value) ? value.reduce((t, r) => t + (Number(arg ? dig(r, arg) : r) || 0), 0) : Number(value) || 0;
        case "money": {
            const n = Number(value);
            return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
        }
        case "number": {
            const n = Number(value);
            return Number.isFinite(n) ? n.toLocaleString("en-AU") : "";
        }
        case "upper":
            return asText(value).toUpperCase();
        case "lower":
            return asText(value).toLowerCase();
        case "date": {
            const d = new Date(typeof value === "number" || typeof value === "string" ? value : NaN);
            return dateMedium(d, { year: false });
        }
        default:
            return value;
    }
}
/** Fill `{{binding.path|pipe|pipe:arg}}` from the bindings' answers. A missing path is an empty string, never an error. */
export function resolveTemplate(text, data) {
    if (!data || !text.includes("{{"))
        return text;
    return text.replace(/\{\{\s*([A-Za-z0-9_.]+)((?:\s*\|\s*[a-z]+(?::[A-Za-z0-9_]+)?)*)\s*\}\}/g, (_m, path, pipes) => {
        let value = dig(data, path);
        for (const p of pipes.split("|").map((s) => s.trim()).filter(Boolean))
            value = applyPipe(value, p);
        return asText(value);
    });
}
/** A table's rows: its literal rows, or a binding's list (capped and flattened to cells). */
export function resolveRows(node, data, max = 200) {
    if (node.rows)
        return node.rows.slice(0, max);
    if (!node.rowsFrom)
        return [];
    const list = dig(data ?? {}, node.rowsFrom);
    if (!Array.isArray(list))
        return [];
    return list.slice(0, max).map((r) => {
        const row = {};
        for (const c of node.columns) {
            const v = r && typeof r === "object" ? dig(r, c.key) : undefined;
            row[c.key] = typeof v === "string" ? v.slice(0, 240) : typeof v === "number" || typeof v === "boolean" ? v : v === null ? null : "";
        }
        return row;
    });
}
const STEP_STATES = ["done", "running", "failed", "waiting", "skipped", "todo"];
/** A `steps` node's stages: its literal ones, or a binding's list of `{ label, state, hint? }` (capped; an unknown state reads as `todo`). */
export function resolveSteps(node, data, max = 24) {
    if (node.steps)
        return node.steps.slice(0, max);
    if (!node.stepsFrom)
        return [];
    const list = dig(data ?? {}, node.stepsFrom);
    if (!Array.isArray(list))
        return [];
    const out = [];
    for (const r of list.slice(0, max)) {
        if (!r || typeof r !== "object")
            continue;
        const label = asText(r.label).slice(0, 120);
        if (!label)
            continue;
        const raw = asText(r.state);
        const hint = asText(r.hint).slice(0, 240);
        out.push({ label, state: (STEP_STATES.includes(raw) ? raw : "todo"), ...(hint ? { hint } : {}) });
    }
    return out;
}
/** A `progress` node's value as a number: a literal, or its placeholder filled; anything that is not a number reads as 0. */
export function progressValue(node, data) {
    const n = typeof node.value === "number" ? node.value : Number(resolveTemplate(node.value, data) || NaN);
    return Number.isFinite(n) && n > 0 ? n : 0;
}
/**
 * A microapp's screen as a block in a conversation (the `conversation` placement): its text filled from the bindings' answers once,
 * its tables' `rowsFrom` turned into the rows they had, and the buttons that `run` something dropped (a conversation block has no
 * bindings to re-read and no declared actions). What is left is an ordinary block.
 */
export function bakeSpec(spec, data) {
    const fill = (s) => resolveTemplate(s, data);
    const bake = (n) => {
        switch (n.type) {
            case "stack":
            case "card": {
                const children = n.children.map(bake).filter((c) => !!c);
                return n.type === "card" ? { ...n, ...(n.title ? { title: fill(n.title) } : {}), children } : { ...n, children };
            }
            case "text":
                return { ...n, text: fill(n.text) };
            case "heading":
                return { ...n, text: fill(n.text) };
            case "chip":
                return { ...n, text: fill(n.text) };
            case "stat":
                return { ...n, label: fill(n.label), value: fill(String(n.value)), ...(n.hint ? { hint: fill(n.hint) } : {}) };
            case "button":
                return n.action.do === "run" ? undefined : { ...n, label: fill(n.label) };
            case "actions": {
                const buttons = n.buttons.filter((b) => b.action.do !== "run").map((b) => ({ ...b, label: fill(b.label) }));
                return buttons.length ? { ...n, buttons } : undefined;
            }
            case "table": {
                const rows = resolveRows(n, data);
                const { rowsFrom: _from, rowActions, ...rest } = n;
                const kept = (rowActions ?? []).filter((a) => a.action.do !== "run");
                return { ...rest, rows, ...(kept.length ? { rowActions: kept } : {}) };
            }
            case "steps": {
                const { stepsFrom: _from, ...rest } = n;
                return { ...rest, ...(n.label ? { label: fill(n.label) } : {}), steps: resolveSteps(n, data) };
            }
            case "progress":
                return { ...n, ...(n.label ? { label: fill(n.label) } : {}), value: progressValue(n, data) };
            default:
                return bakeControl(n);
        }
    };
    const bakeControl = (n) => {
        const out = {};
        for (const [k, v] of Object.entries(n))
            out[k] = k === "children" || k === "panels" || k === "rows" ? v : fillDeep(v, fill);
        if (n.children)
            out.children = n.children.map(bake).filter((c) => !!c);
        if (n.panels)
            out.panels = n.panels.map((p) => ({ ...p, label: fill(p.label), children: p.children.map(bake).filter((c) => !!c) }));
        if (typeof n.rowsFrom === "string" || Array.isArray(n.rows)) {
            out.rows = resolveRecords(n, data);
            delete out.rowsFrom;
            delete out.keys;
        }
        if (typeof n.optionsFrom === "string") {
            out.options = resolveOptions(n, data);
            delete out.optionsFrom;
            delete out.keys;
        }
        const stripped = stripRun(out);
        return (stripped === DROP ? undefined : stripped);
    };
    return { ...spec, root: bake(spec.root) ?? { type: "text", text: spec.alt } };
}
const DROP = Symbol("drop");
/** Every string below a value, filled from the bindings. */
function fillDeep(v, fill) {
    if (typeof v === "string")
        return fill(v);
    if (Array.isArray(v))
        return v.map((x) => fillDeep(x, fill));
    if (v && typeof v === "object")
        return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, fillDeep(x, fill)]));
    return v;
}
/** A conversation block has no declared actions: an object that is a `run` action goes, and with it the button or row action that held it. */
function stripRun(v) {
    if (Array.isArray(v))
        return v.map(stripRun).filter((x) => x !== DROP);
    if (v && typeof v === "object") {
        if (v.do === "run")
            return DROP;
        const out = {};
        for (const [k, x] of Object.entries(v)) {
            const r = stripRun(x);
            if (r === DROP) {
                if (k === "action")
                    return DROP;
                continue;
            }
            out[k] = r;
        }
        return out;
    }
    return v;
}
/** The plain-text rendering of a spec: its alt line. What a node's chat and a read-only log show. */
export function specFallbackText(spec) {
    return spec.alt;
}
//# sourceMappingURL=uiSpecModel.js.map